import { useState, useEffect, useCallback } from 'react';
import {
  getCarrierAuthorityHistory,
  getCarrierInsuranceHistory,
} from '../services/carrierHistoryApi';
import type {
  AuthorityTimelineResult,
  InsuranceTimelineResult,
} from '../types/carrierHistory';

export type CarrierHistoryKind = 'authority' | 'insurance';

interface UseCarrierHistoryBase {
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export interface UseAuthorityHistoryResult extends UseCarrierHistoryBase {
  data: AuthorityTimelineResult | null;
}

export interface UseInsuranceHistoryResult extends UseCarrierHistoryBase {
  data: InsuranceTimelineResult | null;
}

/**
 * React hook to fetch one of a carrier's reconstructed history timelines.
 *
 * The not-yet-derived cases (registration feeds hold nothing for this
 * carrier, or no dated spans could be built) are NORMAL states, not errors:
 * they arrive as a 200 with `data.status` of 'no_source_data' or
 * 'no_events_derived' plus a human-readable `data.reason`. `error` is only
 * set when the request itself fails.
 *
 * @param dotNumber - The DOT number of the carrier (null disables fetching)
 * @param kind      - Which timeline to fetch: 'authority' or 'insurance'
 *
 * @example
 * ```tsx
 * const { data, isLoading, error, refetch } = useCarrierHistory(965044, 'authority');
 * ```
 */
export function useCarrierHistory(
  dotNumber: number | null,
  kind: 'authority'
): UseAuthorityHistoryResult;
export function useCarrierHistory(
  dotNumber: number | null,
  kind: 'insurance'
): UseInsuranceHistoryResult;
export function useCarrierHistory(
  dotNumber: number | null,
  kind: CarrierHistoryKind
): UseAuthorityHistoryResult | UseInsuranceHistoryResult {
  const [data, setData] = useState<AuthorityTimelineResult | InsuranceTimelineResult | null>(
    null
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchHistory = useCallback(async () => {
    if (!dotNumber) {
      setError('No DOT number provided');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const result =
        kind === 'authority'
          ? await getCarrierAuthorityHistory(dotNumber)
          : await getCarrierInsuranceHistory(dotNumber);
      setData(result);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load carrier history';
      setError(errorMessage);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [dotNumber, kind]);

  useEffect(() => {
    if (!dotNumber) return;
    fetchHistory();
  }, [dotNumber, fetchHistory]);

  return { data, isLoading, error, refetch: fetchHistory } as
    | UseAuthorityHistoryResult
    | UseInsuranceHistoryResult;
}

export default useCarrierHistory;
